import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Button, Select, Space, theme } from 'antd';
import type { EventBus, PDFViewer } from 'pdfjs-dist/web/pdf_viewer.mjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { MAX_ZOOM, MIN_ZOOM, nextZoomStep, ZOOM_PRESETS } from './zoomLevels';

interface Props {
  viewer: PDFViewer | null;
  eventBus: EventBus | null;
}

const selectStyle = css`
  width: 150px;
`;

export const ZoomControls: React.FC<Props> = ({ viewer, eventBus }) => {
  const { token } = theme.useToken();
  const [scaleValue, setScaleValue] = useState<string>('auto');
  const [scale, setScale] = useState<number>(1);

  useEffect(() => {
    if (!eventBus || !viewer) {
      return;
    }
    const onScaleChanging = () => {
      setScaleValue(viewer.currentScaleValue);
      setScale(viewer.currentScale);
    };
    eventBus.on('scalechanging', onScaleChanging);
    // The initial scale is applied on 'pagesinit', which can fire before this component
    // sees the new viewer. Read it once so the dropdown never shows a stale mode.
    if (viewer.pdfDocument) {
      onScaleChanging();
    }
    return () => eventBus.off('scalechanging', onScaleChanging);
  }, [eventBus, viewer]);

  const apply = (value: string) => {
    if (!viewer) {
      return;
    }
    viewer.currentScaleValue = value;
    setScaleValue(value);
  };

  const options = ZOOM_PRESETS.map(preset => ({ value: preset.value, label: preset.label }));
  const isPreset = options.some(option => option.value === scaleValue);
  const selectValue = isPreset ? scaleValue : `${Math.round(scale * 100)} %`;

  return (
    <Space size={token.paddingXS}>
      <Button
        icon={<ZoomOutOutlined />}
        aria-label="Verkleinern"
        disabled={!viewer || scale <= MIN_ZOOM}
        onClick={() => apply(String(nextZoomStep(scale, -1)))}
      />
      <Button
        icon={<ZoomInOutlined />}
        aria-label="Vergrößern"
        disabled={!viewer || scale >= MAX_ZOOM}
        onClick={() => apply(String(nextZoomStep(scale, 1)))}
      />
      <Select className={selectStyle} value={selectValue} options={options} disabled={!viewer} onChange={apply} />
    </Space>
  );
};
